import React from 'react';

import { ActionIcon, Box, CopyButton, Flex, Paper, Space, Text } from '@mantine/core';
import { Prism } from '@mantine/prism';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import { InfoComponent } from 'components/InfoComponent';
import { config } from 'environment/config';
import { useTranslation } from 'react-i18next';
import { ROUTE_LINKS } from 'shared/constants/route-links';
import { WhiteLabelIntegration } from 'shared/interfaces/SmartAssistant';
import { buildDomainUrl } from 'shared/utils/domain';

interface SmartAssistantWebsiteCodeSnipetProps {
  smartAssistantId: string;
  onCopied?: () => void;
  whiteLabelIntegration?: WhiteLabelIntegration;
}
export function SmartAssistantWebsiteCodeSnipet({
  smartAssistantId,
  onCopied,
  whiteLabelIntegration
}: SmartAssistantWebsiteCodeSnipetProps) {
  const { t } = useTranslation();

  const getDomain = () => {
    if (!whiteLabelIntegration) return window.location.origin;

    return buildDomainUrl(whiteLabelIntegration.subDomain, whiteLabelIntegration.domain);
  };

  const iFrameCode =
    `<iframe
    src="${getDomain()}${ROUTE_LINKS.guestSmartAssistant}/${smartAssistantId}"
    width="100%"
    height="100%"
    allow="microphone"
    frameborder="0">` + `\n</iframe>`;

  const bubbleCode =
    `<script
    id="${smartAssistantId}"
    src="${getDomain()}/${config.chatScriptName}"
    defer>` + `\n</script>`;

  return (
    <Box>
      <Box>
        <Flex direction="row" align="center" mb={3}>
          <Text mr={5}>{t('SMART_ASSISTANT.WEBSITE.AS_IFRAME.LABEL')}</Text>
          <InfoComponent contentTranslationKey={'SMART_ASSISTANT.WEBSITE.AS_IFRAME.INFO'} />
        </Flex>

        <Paper
          withBorder
          sx={{
            zIndex: 30,
            '&:hover': {
              cursor: 'auto !important'
            }
          }}>
          <Prism language="jsx" noCopy>
            {iFrameCode}
          </Prism>
          <Box mt={-40} mr={10} sx={{ float: 'right' }}>
            <CopyButton value={iFrameCode} timeout={2000}>
              {({ copied, copy }) => (
                <ActionIcon
                  onClick={() => {
                    if (onCopied) onCopied();
                    copy();
                  }}>
                  {copied ? <IconCheck color="green" /> : <IconCopy color="black" />}
                </ActionIcon>
              )}
            </CopyButton>
          </Box>
        </Paper>
      </Box>
      <Space h="md" />
      <Box>
        <Flex direction="row" align="center" mb={3}>
          <Text mr={5}>{t('SMART_ASSISTANT.WEBSITE.AS_BUBBLE.LABEL')}</Text>
          <InfoComponent contentTranslationKey={'SMART_ASSISTANT.WEBSITE.AS_BUBBLE.INFO'} />
        </Flex>
        <Paper
          withBorder
          sx={{
            '&:hover': {
              cursor: 'auto !important'
            }
          }}>
          <Prism language="jsx" noCopy>
            {bubbleCode}
          </Prism>
          <Box mt={-40} mr={10} sx={{ float: 'right' }}>
            <CopyButton value={bubbleCode} timeout={2000}>
              {({ copied, copy }) => (
                <ActionIcon
                  color="black"
                  onClick={() => {
                    if (onCopied) onCopied();

                    copy();
                  }}>
                  {copied ? <IconCheck color="green" /> : <IconCopy color="black" />}
                </ActionIcon>
              )}
            </CopyButton>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
